import { memo, useCallback, useMemo } from "react";
import styled from "styled-components";
import { CheckIcon, MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Button, TokenDeck } from "@sablier/v2-components/molecules";
import { chains } from "@sablier/v2-constants";
import { tags } from "@sablier/v2-constants";
import { useChainExplorer, useShortAddress } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { IToken } from "@sablier/v2-types";
import { areEqual } from "react-window";
import type { CSSProperties } from "react";

export enum ItemAction {
  ACCEPT = "Accept Token",
  CHOOSE = "Choose",
  REMOVE = "Remove",
  SAVE = "Save Token",
}

const Action = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    column-gap: 12px;
    opacity: 1;
    & > p {
      opacity: 0;
      ${(props) => props.theme.styles.textParagraphMini}
      & {
        color: ${(props) => props.theme.colors.dark900};
      }
    }
  }
`;

const Asset = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    flex: 1;
  }
`;

const Wrapper = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 0 8px;
    border-radius: 6px;

    &:hover,
    &:active {
      ${Action} {
        opacity: 1;
        & > p {
          opacity: 1;
        }
      }
    }

    &[data-action="${ItemAction.CHOOSE}"][data-selected="true"] {
      &,
      &:hover,
      &:active {
        background-color: ${(props) => props.theme.colors.dark400};
        cursor: default;
      }
    }

    &[data-action="${ItemAction.CHOOSE}"][data-local="true"] {
      ${Action} {
        opacity: 1;
        & > p {
          opacity: 1;

          &:before {
            content: "Saved by user |";
            padding-right: 10px;
            color: ${(props) => props.theme.colors.gray500};
          }
        }
      }
    }

    &[data-action="${ItemAction.CHOOSE}"],
    &[data-action="${ItemAction.SAVE}"]:not([data-saved="true"]),
    &[data-action="${ItemAction.ACCEPT}"]:not([data-saved="true"]) {
      cursor: pointer;
      &:hover,
      &:active {
        background-color: ${(props) => props.theme.colors.dark300};
      }
    }

    *[data-component="button"] {
      cursor: pointer;
    }

    & > * {
      z-index: 10;
    }
  }

  ${(props) => props.theme.medias.maxSM} {
    ${Action} {
      & > p:first-child {
        display: none;
      }
    }
    ${Asset} {
      *[data-purpose="image"] {
        --size: 26px;
      }

      a {
        p {
          font-size: 9pt;
        }
      }
    }
  }
`;

interface Props {
  data: { action: ItemAction; list: IToken[]; onClick: (item: IToken) => void };
  index: number;
  style?: CSSProperties;
}

interface ActionProps {
  item: IToken;
  data: Props["data"];
}
function getItemActionTranslateKey(action: ItemAction) {
  switch (action) {
    case ItemAction.ACCEPT:
      return "modalCards.token.acceptAction";
    case ItemAction.CHOOSE:
      return "modalCards.token.chooseAction";
    case ItemAction.REMOVE:
      return "modalCards.token.removeAction";
    case ItemAction.SAVE:
      return "modalCards.token.saveAction";
  }
}

function ActionsAccept({ data, item }: ActionProps) {
  const { t } = useT();
  return item.isLocal || item.isWhitelisted ? (
    <Button
      accent={"secondaryDesaturated"}
      appearance={"transparent"}
      isMini
      isPreview
      right={CheckIcon}
      title={_.capitalize(item.isLocal ? t("words.saved") : t("words.listed"))}
      titleShort={""}
    />
  ) : (
    <Button
      accent={"secondaryDesaturated"}
      appearance={"outline"}
      isMini
      isPreview
      right={PlusIcon}
      title={t(getItemActionTranslateKey(data.action))}
      titleShort={""}
    />
  );
}

function ActionsRemove({ item, data }: ActionProps) {
  const { t } = useT();
  return (
    <>
      <Button
        accent={"warning"}
        appearance={"outline"}
        isMini
        onClick={() => data.onClick(item)}
        right={MinusIcon}
        title={t(getItemActionTranslateKey(data.action))}
        titleShort={""}
      />
    </>
  );
}

function ActionsSave({ item, data }: ActionProps) {
  const { t } = useT();
  return (
    <>
      {item.isLocal || item.isWhitelisted ? (
        <Button
          accent={"gray100"}
          appearance={"transparent"}
          isMini
          isPreview
          right={CheckIcon}
          title={_.capitalize(
            item.isLocal ? t("words.saved") : t("words.listed"),
          )}
          titleShort={""}
        />
      ) : (
        <Button
          accent={"iconic400"}
          appearance={"outline"}
          isMini
          isPreview
          right={PlusIcon}
          title={t(getItemActionTranslateKey(data.action))}
          titleShort={""}
        />
      )}
    </>
  );
}

const Item = memo(function Row({ data, index, style }: Props) {
  const item = useMemo(() => data.list[index], [data, index]);
  const short = useShortAddress(item?.address);

  const key = useMemo(
    () => [item.address, item.chainId, item.name].map((i) => i || "").join("-"),
    [item],
  );

  const onClick = useCallback(() => {
    if (!data || !item) {
      return;
    }
    if (!_.isFunction(data.onClick)) {
      return;
    }

    if (data.action !== ItemAction.CHOOSE) {
      if (item.isLocal || item.isWhitelisted) {
        return;
      }
    }

    data.onClick(item);
  }, [data, item]);

  const description = useMemo(() => {
    if (!data || !item) {
      return undefined;
    }

    const base = item.name.endsWith(" Token")
      ? item.name.substring(0, item.name.length - 6)
      : item.name;

    if (data.action === ItemAction.REMOVE) {
      const chain = chains[item.chainId];
      if (chain && chain.name) {
        return base.concat(" · ").concat(chain.name);
      }
    }

    return base;
  }, [data, item]);

  const explorer = useChainExplorer({
    chainId: item.chainId,
    type: "token",
    hash: item.address,
  });

  if (!data || !item) {
    return false;
  }

  return (
    <Wrapper
      data-action={data.action}
      data-saved={item.isLocal || item.isWhitelisted}
      data-local={item.isLocal}
      data-selected={item.isSelected}
      key={key}
      onClick={onClick}
      style={style}
      data-cy={tags.actionTokenPicker}
    >
      <Asset>
        <TokenDeck
          ellipsis={230}
          explorer={explorer}
          description={description}
          image={item.image}
          isFallbackShown
          title={item.symbol}
        />
      </Asset>
      <Action>
        <p>{short}</p>
        {data.action === ItemAction.ACCEPT && (
          <ActionsAccept data={data} item={item} />
        )}
        {data.action === ItemAction.SAVE && (
          <ActionsSave data={data} item={item} />
        )}
        {data.action === ItemAction.REMOVE && (
          <ActionsRemove data={data} item={item} />
        )}
      </Action>
    </Wrapper>
  );
}, areEqual);

export default Item;
